/* eslint-disable no-unused-vars */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import axiosApis from 'common/apis/axiosApis';
import { queryClient } from 'common/const/queryClient';

// Active-aircraft filter options for the Accounts list (BT-9796). These string values are shared with
// the backend (getAllAccountsData.js) — keep them in sync.
export const ACTIVE_AIRCRAFT_FILTER = {
  ALL: 'all', // no filter (default)
  HIDE_NONE: 'hide_none', // hide accounts with no active aircraft
  ONLY_NONE: 'only_none', // show only accounts with no active aircraft
};

export const useAllAccountsParamStore = create((set) => ({
  min: 0,
  max: 50,
  searchText: '',
  activePage: 1,
  // Extra filters (BT-9326). `style`/`billed` use '' to mean "any".
  featureKeys: [],
  activeAircraftFilter: ACTIVE_AIRCRAFT_FILTER.ALL,
  style: '',
  billed: '',
  tierId: '',
  setMin: (min) => set({ min }),
  setMax: (max) => set({ max }),
  setSearchText: (searchText) => set({ searchText }),
  setActivePage: (activePage) => set({ activePage }),
  setFeatureKeys: (featureKeys) => set({ featureKeys }),
  setActiveAircraftFilter: (activeAircraftFilter) => set({ activeAircraftFilter }),
  setStyle: (style) => set({ style }),
  setBilled: (billed) => set({ billed }),
  setTierId: (tierId) => set({ tierId }),
  reset: () => set({
    min: 0,
    max: 50,
    searchText: '',
    activePage: 1,
    featureKeys: [],
    activeAircraftFilter: ACTIVE_AIRCRAFT_FILTER.ALL,
    style: '',
    billed: '',
    tierId: '',
  }),
}));

async function getAllAccountsData({ queryKey }) {
  const [_, {
    searchText, min, max, featureKeys, activeAircraftFilter, style, billed, tierId,
  }] = queryKey;
  const userDetails = JSON.parse(localStorage.getItem('adminDetails'));

  const params = new URLSearchParams({ min, max, search_text: searchText });
  if (featureKeys && featureKeys.length > 0) {
    params.set('feature_keys', featureKeys.join(','));
  }
  if (activeAircraftFilter && activeAircraftFilter !== ACTIVE_AIRCRAFT_FILTER.ALL) {
    params.set('active_aircraft_filter', activeAircraftFilter);
  }
  if (style) {
    params.set('style', style);
  }
  if (billed !== '') {
    params.set('billed', billed);
  }
  if (tierId) {
    params.set('tier_id', String(tierId));
  }

  return axiosApis.get(`/admin/getAllAccountsData?${params.toString()}`, {
    headers: {
      Authorization: userDetails.token,
    },
  }).then(async (response) => {
    await response;
    return response;
  });
}

export const getQueryKey = ({
  searchText,
  min,
  max,
  activePage,
  featureKeys,
  activeAircraftFilter,
  style,
  billed,
  tierId,
}) => ['getAllAccountsData', {
  searchText,
  min,
  max,
  activePage,
  featureKeys,
  activeAircraftFilter,
  style,
  billed,
  tierId,
}];

export const invalidateAllAccounts = (params) => {
  if (params) {
    queryClient.invalidateQueries(getQueryKey(params));
  } else {
    queryClient.invalidateQueries(['getAllAccountsData']);
  }
};

export function useAllAccounts() {
  const queryParams = useAllAccountsParamStore(useShallow(
    (state) => ({
      min: state.min,
      max: state.max,
      searchText: state.searchText,
      activePage: state.activePage,
      featureKeys: state.featureKeys,
      activeAircraftFilter: state.activeAircraftFilter,
      style: state.style,
      billed: state.billed,
      tierId: state.tierId,
    }),
  ));

  const { reset } = useAllAccountsParamStore(useShallow((state) => ({
    reset: state.reset,
  })));

  const key = useMemo(() => getQueryKey(queryParams), [queryParams]);

  const {
    isPending, isError, error, data, isFetching, isPlaceholderData,
  } = useQuery({
    queryKey: key,
    queryFn: getAllAccountsData,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 15,
  });

  // When this unmounts, reset the params store
  useEffect(() => () => {
    reset();
  }, [reset]);

  return useMemo(() => ({
    isPending,
    isError,
    error,
    isFetching,
    isPlaceholderData,
    allAccounts: data?.data?.manageAccountsAndUsers || [],
    totalAccountCount: data?.data?.totalAccountCount || 0,
  }), [data, error, isError, isFetching, isPending, isPlaceholderData]);
}
